import React, { useMemo, useState } from "react";
import { Plus, Sparkles, X } from "lucide-react";
import { Modal, ModalTitle } from "./modal";



function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}



function SectionLabel({ children }) {
  return (
    <div className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500">
      {children}
    </div>
  );
}

function PlaceholderChip({ label, deleteChip }) {
  return (
    <span onClick={deleteChip} className="rounded bg-sky-100 px-1 py-0.5 text-[13px] font-semibold text-sky-800 cursor-pointer">
      {`{{${label}}}`}
    </span>
  );
}

function TemplateParagraph({ content, deleteChip, setContent }) {
  const tokens = useMemo(() => {
    return content.split(/(\{\{[^}]+\}\})/g).filter(Boolean);
  }, [content]);

  return (
    <p onInput={(event) => setContent(event.currentTarget.textContent)} contentEditable className="text-sm leading-relaxed text-neutral-800">
      {tokens.map((token, index) => {
        const isPlaceholder = /^\{\{[^}]+\}\}$/.test(token);

        if (!isPlaceholder) {
          return <span  contentEditable={true} key={`${token}-${index}`}>{token}</span>;
        }

        const label = token.replace(/[{}]/g, "");
        return <PlaceholderChip deleteChip={() => deleteChip(label)} key={`${label}-${index}`} label={label} />;
      })}
    </p>
  );
}

function HelperNote({ children }) {
  return (
    <div className="mt-5 border-l-2 border-sky-200 pl-4 italic text-neutral-400">
      <p className="text-xs leading-relaxed">{children}</p>
    </div>
  );
}

function MessageEditor({ content, deleteChip, setContent }) {
  return (
    <div className="min-h-[160px] rounded-xl bg-slate-100 p-5">
      <TemplateParagraph setContent={(value) => setContent(value)} deleteChip={(value) => deleteChip(value)} content={content} />
      <HelperNote>Klik op de variabelen om ze in te voegen op de cursorpositie...</HelperNote>
    </div>
  );
}

function PlaceholderButton({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick(label)}
      className="flex w-full items-center gap-2 rounded-lg bg-white px-3 py-2.5 text-left text-xsm font-medium text-sky-800  ring-1 ring-black/5 transition hover:-translate-y-0.5 hover:shadow-md"
    >
      <Plus className="h-4 w-4 text-sky-700" />
      <span>{`{{${label}}}`}</span>
    </button>
  );
}

function PlaceholderPanel({ placeholders, onAddPlaceholder }) {
  return (
    <div className="rounded-xl bg-slate-100 p-3">
      <h2 className="mb-2 text-sm font-semibold text-neutral-900">Variabelen</h2>
      <div className="space-y-2.5 md:scroll-auto hide-scrollbar max-h-[200px] overflow-y-auto">
        {placeholders.map((placeholder) => (
          <PlaceholderButton
            key={placeholder}
            label={placeholder}
            onClick={onAddPlaceholder}
          />
        ))}
      </div>
    </div>
  );
}

function AdherenceBadge() {
  return (
    <div className="inline-flex items-center rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-sky-800">
      Enhanced Mode
    </div>
  );
}

function SegmentedControlButton({ label, isActive, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "flex-1 rounded-lg px-3 py-2 text-sm font-medium transition",
        isActive
          ? "bg-sky-700 text-white shadow-md"
          : "bg-transparent text-neutral-600 hover:bg-white"
      )}
    >
      {label}
    </button>
  );
}

function AdherenceSelector({ value, onChange, options }) {
  return (
    <div className="rounded-xl bg-slate-100 p-1.5 ">
      <div className="flex gap-1.5">
        {options.map((option) => (
          <SegmentedControlButton
            key={option.id}
            label={option.label}
            isActive={value === option.id}
            onClick={() => onChange(option.id)}
          />
        ))}
      </div>
    </div>
  );
}

function Footnote({ children }) {
  return <p className="text-xs italic text-neutral-500">{children}</p>;
}






export function MessageContentSection({ content, placeholders, onAddPlaceholder, deleteChip, setContent }) {
  return (
    <section>
      <div className="grid gap-4 lg:grid-cols-[1.8fr_0.82fr]">
        <MessageEditor setContent={(value) => setContent(value)} content={content} deleteChip={(value) => deleteChip(value)} />
        <PlaceholderPanel
          placeholders={placeholders}
          onAddPlaceholder={onAddPlaceholder}
        />
      </div>
    </section>
  );
}

export function AdherenceSection({ value, onChange }) {
      const ADHERENCE_OPTIONS = [
    { id: "Original", label: "Alleen origineel" },
    { id: "Balanced", label: "Gebalanceerd" },
    { id: "Creative", label: "Creatief AI" },
  ];
  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-3">
        <SectionLabel>AI Balans</SectionLabel>
        <AdherenceBadge />
      </div>
      <div className="grid gap-4 ">
        <div className="space-y-3">
          <AdherenceSelector
            value={value}
            onChange={onChange}
            options={ADHERENCE_OPTIONS}
          />
          <Footnote>
            * "Alleen origineel" betekent dat de output zo dicht mogelijk bij de oorspronkelijke tekst blijft, terwijl "Creatief AI" meer vrijheid geeft aan de AI om de inhoud aan te passen en te verbeteren. 
          </Footnote>
        </div>
        
      </div>
    </section>
  );
}

